import React, { useState, useCallback } from 'react';
import { googleAuth } from '../services/googleAuth';
import { googleDrive } from '../services/googleDrive';
import { useAuthStore } from '../stores/authStore';
import { ConfirmModal } from './ConfirmModal';
import './RecoveryScreen.css';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';

type RecoveryState = 'idle' | 'searching' | 'found' | 'found-trashed' | 'not-found' | 'creating' | 'confirming';

interface RecoveryScreenProps {
  onRecovered: () => void;
}

export function RecoveryScreen({ onRecovered }: RecoveryScreenProps) {
  const [state, setState] = useState<RecoveryState>('idle');

  const handleSearch = useCallback(async () => {
    setState('searching');
    try {
      const token = await googleAuth.ensureFreshToken();
      if (!token) {
        setState('not-found');
        return;
      }

      // Broad search: includes trashed items
      const query = "name='Bigwa_App' and mimeType='application/vnd.google-apps.folder'";
      const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,name,trashed)`;

      const response = await fetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        setState('not-found');
        return;
      }

      const data = await response.json();

      if (!data.files || data.files.length === 0) {
        setState('not-found');
        return;
      }

      // Check if any non-trashed folder exists
      const activeFolder = data.files.find((f: { trashed: boolean }) => !f.trashed);
      if (activeFolder) {
        setState('found');
        return;
      }

      // Only trashed folders found
      setState('found-trashed');
    } catch {
      setState('not-found');
    }
  }, []);

  const handleReconnect = useCallback(() => {
    googleDrive.reset();
    onRecovered();
  }, [onRecovered]);

  const handleCreateNew = useCallback(() => {
    setState('confirming');
  }, []);

  const handleConfirmCreate = useCallback(() => {
    setState('creating');
    googleDrive.reset();
    onRecovered();
  }, [onRecovered]);

  const handleCancelCreate = useCallback(() => {
    setState('idle');
  }, []);

  const handleLogout = useCallback(() => {
    const { logout } = useAuthStore.getState();
    logout();
    googleDrive.reset();
    window.location.reload();
  }, []);

  return (
    <div className="recovery-screen">
      <div className="recovery-card">
        {/* Icon: folder with question mark */}
        <div className="recovery-card__icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 12C6 9.79 7.79 8 10 8H18L22 12H38C40.21 12 42 13.79 42 16V36C42 38.21 40.21 40 38 40H10C7.79 40 6 38.21 6 36V12Z" fill="#477B72" opacity="0.15" stroke="#477B72" strokeWidth="2" strokeLinejoin="round"/>
            <text x="24" y="32" textAnchor="middle" fill="#477B72" fontSize="18" fontWeight="600" fontFamily="-apple-system, sans-serif">?</text>
          </svg>
        </div>

        <h2 className="recovery-card__title">Pasta não encontrada</h2>
        <p className="recovery-card__description">
          Não conseguimos localizar sua pasta Bigwa_App no Google Drive.
          Isso pode acontecer se ela foi movida, renomeada ou excluída.
        </p>

        {/* Status messages for search results */}
        {state === 'found' && (
          <div className="recovery-card__status recovery-card__status--found">
            Pasta localizada!
          </div>
        )}
        {state === 'found-trashed' && (
          <div className="recovery-card__status recovery-card__status--found-trashed">
            Pasta encontrada na lixeira do Google Drive. Restaure-a e tente novamente.
          </div>
        )}
        {state === 'not-found' && (
          <div className="recovery-card__status recovery-card__status--not-found">
            Nenhuma pasta encontrada. Verifique a Lixeira do Drive ou crie um novo ambiente.
          </div>
        )}

        <div className="recovery-card__actions">
          {/* Primary action: search or reconnect */}
          {state === 'found' ? (
            <button className="recovery-card__btn-primary" onClick={handleReconnect}>
              Reconectar
            </button>
          ) : (
            <button
              className="recovery-card__btn-primary"
              onClick={handleSearch}
              disabled={state === 'searching' || state === 'creating'}
            >
              {state === 'searching' && <span className="recovery-spinner" />}
              {state === 'searching' ? 'Procurando...' : 'Tentar Localizar'}
            </button>
          )}

          {/* Secondary action: create new */}
          {state !== 'found' && (
            <button
              className="recovery-card__btn-secondary"
              onClick={handleCreateNew}
              disabled={state === 'searching' || state === 'creating'}
            >
              {state === 'creating' && <span className="recovery-spinner" />}
              {state === 'creating' ? 'Criando...' : 'Criar Novo Ambiente'}
            </button>
          )}

          {/* Logout link */}
          <button className="recovery-card__btn-logout" onClick={handleLogout}>
            Fazer Logout
          </button>
        </div>
      </div>

      {/* Confirmation modal for creating new environment */}
      <ConfirmModal
        isOpen={state === 'confirming'}
        title="Criar novo ambiente?"
        message="Isso criará uma nova pasta vazia no Google Drive. Seus dados anteriores não serão perdidos, mas o Bigwa não poderá acessá-los até que a pasta original seja restaurada."
        confirmLabel="Criar Novo"
        cancelLabel="Cancelar"
        variant="warning"
        onConfirm={handleConfirmCreate}
        onCancel={handleCancelCreate}
      />
    </div>
  );
}
